import React, { ReactNode } from "react";
import { Provider } from "react-redux";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { CacheProvider } from "@emotion/react";
import { PostHogProvider } from "posthog-js/react";

import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import createTheme from "@/theme";
import useThemeVariant from "@/hooks/useThemeVariant";
import { store } from "@/redux/store";
import createEmotionCache from "@/utils/createEmotionCache";
import { posthog } from "@/lib/posthog";

import { AuthProvider } from "@/contexts/KeycloakContext";
import { EntitlementsProvider } from "@/contexts/EntitlementsContext";
import { SnackbarProvider } from "@/contexts/SnackbarContext";
import GlobalStyle from "@/components/GlobalStyle";

const clientSideEmotionCache = createEmotionCache();

interface ProvidersProps {
  children: ReactNode;
  emotionCache?: ReturnType<typeof createEmotionCache>;
}

function AppProviders({
  children,
  emotionCache = clientSideEmotionCache,
}: ProvidersProps) {
  const { theme } = useThemeVariant();

  return (
    <CacheProvider value={emotionCache}>
      <HelmetProvider>
        <Helmet titleTemplate="%s | Atica Portal" defaultTitle="Atica Portal" />
        <PostHogProvider client={posthog}>
          <Provider store={store}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MuiThemeProvider theme={createTheme(theme)}>
                <SnackbarProvider>
                  <AuthProvider>
                    <EntitlementsProvider>
                      <GlobalStyle />
                      {children}
                    </EntitlementsProvider>
                  </AuthProvider>
                </SnackbarProvider>
              </MuiThemeProvider>
            </LocalizationProvider>
          </Provider>
        </PostHogProvider>
      </HelmetProvider>
    </CacheProvider>
  );
}

export default AppProviders;
